import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  service: string;
  budget: string;
  message: string;
  submittedAt: string;
}

export default function ContactManager() {
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['/api/admin/contacts'],
    retry: false,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Contact Submissions</h2>
        <p className="text-slate-600 dark:text-slate-300">View and manage customer inquiries</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {Array.isArray(contacts) && contacts.map((contact: Contact) => (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                        {contact.firstName} {contact.lastName}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
                        <span>{contact.email}</span>
                        <span>{contact.phone}</span>
                        <span>{format(new Date(contact.submittedAt), 'MMM dd, yyyy HH:mm')}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Badge variant="outline">{contact.service}</Badge>
                      <Badge variant="secondary">{contact.budget}</Badge>
                    </div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-700 dark:text-slate-300">{contact.message}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
          
          {(!Array.isArray(contacts) || contacts.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center">
                <i className="fas fa-envelope text-4xl text-slate-300 dark:text-slate-600 mb-4"></i>
                <p className="text-slate-600 dark:text-slate-300">No contact submissions yet.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}